/**
 * Emission factors advanced-filter document — a versioned value the web filter
 * panel (`EmissionFactorFilterPanel`) edits and evaluates CLIENT-SIDE over the
 * complete per-table factor index. One schema + ONE evaluator, both in
 * `@releve/shared` (the places/Impact/AR/AP/bank-feed/Vault/timesheets
 * template), shared by:
 *  - the web filter panel (live evaluation as controls change),
 *  - any future server consumer (reuse `applyEmissionFactorFilterDocument` —
 *    never build SQL WHERE clauses from the document).
 *
 * The document is AD HOC — it lives in component state for the session
 * (saved/shared presets were deliberately removed from the places build; same
 * posture here).
 *
 * ── v1 vocabulary: FIVE FIXED FAMILIES + N DYNAMIC ONES ────────────────────
 * The fixed five are ordinary string families over mapped grid fields:
 * `status`, `scope`, `provenance`, `data_source` and `vintage`.
 *
 * The dynamic ones are this document's one novelty. A factor table defines its
 * OWN dimension columns (`region`, `fuel_type`, `year`, …) — the set is per
 * table and only known at runtime — so they cannot be a static field
 * vocabulary. They are spelled as ONE field, `factor.dimension`, carrying a
 * `key` (the column code): a KEYED string condition. A runtime-built
 * `z.enum([...columnCodes])` was the alternative and was rejected — it would
 * infer `field: string` and throw away the whole point of the enum'd
 * vocabulary for the fixed five.
 *
 * The keyed condition and its ops live HERE, module-local, and are
 * deliberately NOT added to `filterDocumentPrimitives.ts`: exactly one entity
 * needs them today. Promote them the moment a second one does — the
 * primitives' own docblock sets that bar.
 *
 * ── THE SEEDED DEFAULT ─────────────────────────────────────────────────────
 * Like the bank feed (and unlike the other siblings) this document ships a
 * NON-EMPTY default: `DEFAULT_EMISSION_FACTOR_FILTER_DOCUMENT` carries
 * `status in ['active']`, so a fresh grid opens on the live factors and
 * soft-deleted rows stay out of the way behind a "N deleted / Show deleted"
 * notice. Two accepted consequences, both deliberate: the filter badge reads
 * "1" on first load, and Clear resets to
 * `EMPTY_EMISSION_FACTOR_FILTER_DOCUMENT` (every row), NOT back to the default
 * — the sibling convention, so "Clear" always means "no filters".
 *
 * Per docs/INDEX_GRID_PATTERN.md step 9b, a grid runs the panel OR filterable
 * columns, never both: two filter states means the panel silently shrinks the
 * header option lists, each has its own Clear, and the badge counts only one
 * of them. The factors grid ends with ZERO filterable columns.
 *
 * v1 fields are `factor.<field>` over the MAPPED grid rows, not the wire row —
 * evaluation runs AFTER the mapper and BEFORE the grid shell
 * (`hasUpstreamFilters`). The field string carries its namespace, so a later
 * vocabulary stays additive.
 */
import { z } from "zod";
import { makeFilterDocumentSchema, makeStringConditionSchema, matchesStringOp, } from "./filterDocumentPrimitives.js";
// ---------------------------------------------------------------------------
// Field vocabulary (v1)
// ---------------------------------------------------------------------------
/**
 * The five FIXED families.
 *
 * `factor.status` ('active' | 'deleted') and `factor.provenance`
 * ('climatiq' | 'system' | 'custom') are CLOSED vocabularies derived by the
 * mapper, but stay plain string families here — `values` is the panel's
 * selection and the row accessors are typed `string`, so a narrower mapper
 * union satisfies them structurally (the `timesheet.visibility` precedent).
 *
 * `factor.vintage` is the year a factor applies to: the table's `year`
 * dimension when it has one, else the first four characters of
 * `effective_date`. Always a plain `yyyy` STRING, never a date — it is
 * checkbox-selected, not range-compared, so this document carries no date
 * family at all.
 */
export const EMISSION_FACTOR_FILTER_STRING_FIELDS = [
    "factor.status",
    "factor.scope",
    "factor.provenance",
    "factor.data_source",
    "factor.vintage",
];
/**
 * The ONE keyed field. Its `key` is a dimension column's `column_code`, so the
 * vocabulary is per table and open — see the docblock.
 */
export const EMISSION_FACTOR_FILTER_DIMENSION_FIELD = "factor.dimension";
// ---------------------------------------------------------------------------
// Conditions
// ---------------------------------------------------------------------------
/**
 * TWO families, so a two-armed union. The arms have DISJOINT op sets
 * ('in'/'is_empty' vs 'keyed_in'/'keyed_is_empty'), which is what lets
 * `matchesCondition` narrow on `op` ALONE. Reusing the string family's op
 * names for the keyed arm would still TYPE-CHECK — a union of object shapes —
 * while routing a keyed condition into the fixed-field accessor, whose switch
 * has no `factor.dimension` case; namespaced ops make that unrepresentable.
 *
 * EMPTY-SET SEMANTICS (from the shared primitives, not re-decided here): an
 * `in` condition with zero values is REJECTED by the schema. "Nothing checked"
 * is spelled as NO CONDITION AT ALL — the panel drops the family — and never
 * as a condition that would match nothing. The keyed arm restates the same
 * three refines for the same reasons.
 */
const stringConditionSchema = makeStringConditionSchema(EMISSION_FACTOR_FILTER_STRING_FIELDS);
/**
 * A string condition on ONE dimension column, identified by `key`.
 *
 * `key` is a `column_code` (`max(100)` — the column codes are short slugs).
 * An UNKNOWN key is deliberately still VALID: a table's columns can be renamed
 * or removed while a document sits in component state, and the panel keeps
 * conditions it does not recognise in a passthrough list and re-emits them
 * verbatim rather than silently dropping a user's filter. The evaluator reads
 * a missing key as `''`, so such a condition matches only rows the widening
 * below reaches — it narrows, it never widens by accident.
 */
const keyedStringConditionSchema = z
    .object({
    field: z.literal(EMISSION_FACTOR_FILTER_DIMENSION_FIELD),
    /** The dimension column's `column_code`. */
    key: z.string().min(1).max(100),
    op: z.enum(["keyed_in", "keyed_is_empty"]),
    values: z.array(z.string().min(1).max(200)).min(1).max(50).optional(),
    /** `keyed_in` only: ALSO match rows whose dimension value is unset. */
    include_empty: z.boolean().optional(),
})
    .refine((c) => c.op !== "keyed_in" || (c.values?.length ?? 0) > 0, {
    message: "op 'keyed_in' requires a non-empty values array",
})
    .refine((c) => c.op !== "keyed_is_empty" || c.include_empty == null, {
    message: "op 'keyed_is_empty' already selects empty — drop include_empty",
})
    .refine((c) => c.op !== "keyed_is_empty" || c.values == null, {
    message: "op 'keyed_is_empty' selects unset rows — drop values (they are dead)",
});
export const emissionFactorFilterConditionSchema = z.union([
    stringConditionSchema,
    keyedStringConditionSchema,
]);
// ---------------------------------------------------------------------------
// The document
// ---------------------------------------------------------------------------
export const emissionFactorFilterDocumentSchema = makeFilterDocumentSchema(emissionFactorFilterConditionSchema);
/** The empty document — "no filters active" (what Clear resets to). */
export const EMPTY_EMISSION_FACTOR_FILTER_DOCUMENT = {
    version: 1,
    match: "all",
    conditions: [],
};
/**
 * What a FRESH grid starts on: the live factors only. See the docblock for the
 * two accepted consequences (badge reads 1 on load; Clear goes to EMPTY, not
 * back to here).
 */
export const DEFAULT_EMISSION_FACTOR_FILTER_DOCUMENT = {
    version: 1,
    match: "all",
    conditions: [{ field: "factor.status", op: "in", values: ["active"] }],
};
/*
 * One accessor per field FAMILY. The fixed one is an exhaustive switch over
 * its vocabulary with NO default case (the reason the siblings keep them: a
 * new vocabulary entry is a `TS2366: Function lacks ending return statement`
 * until it is handled here, instead of compiling clean and filtering the wrong
 * column).
 */
function stringValue(row, field) {
    switch (field) {
        case "factor.status":
            // Mapper-derived, never null — no `?? ''` fallback.
            return row.status;
        case "factor.scope":
            return row.scope ?? "";
        case "factor.provenance":
            // Mapper-derived, never null.
            return row.provenance;
        case "factor.data_source":
            return row.data_source ?? "";
        case "factor.vintage":
            return row.vintage ?? "";
    }
}
/**
 * The dimension value for one column code. A column the factor carries no
 * value for — or one that no longer exists on the table — reads as `''`, the
 * same way a null string field does, so `keyed_is_empty` and the
 * `include_empty` widening reach it and a plain `keyed_in` never does.
 */
function dimensionValue(row, key) {
    return row.dimension_values[key] ?? "";
}
/**
 * `keyed_in` / `keyed_is_empty` semantics — IDENTICAL to the string family's,
 * delegated to its one implementation. The ops are namespaced only so the
 * evaluator can narrow on `op` alone (see the condition docblock); nothing
 * about how they MATCH differs.
 */
function matchesKeyedStringOp(op, value, values, includeEmpty) {
    return matchesStringOp(op === "keyed_in" ? "in" : "is_empty", value, values, includeEmpty);
}
// The two condition families have disjoint op sets, so op alone narrows.
function matchesCondition(row, c) {
    switch (c.op) {
        case "in":
        case "is_empty":
            return matchesStringOp(c.op, stringValue(row, c.field), c.values, c.include_empty);
        case "keyed_in":
        case "keyed_is_empty":
            return matchesKeyedStringOp(c.op, dimensionValue(row, c.key), c.values, c.include_empty);
    }
}
/**
 * Apply a filter document to mapped factor rows (AND of all conditions — v1
 * `match` is 'all' only). An empty document returns the input array unchanged
 * (identity — callers can rely on reference equality to skip re-renders).
 *
 * TWO conditions on two different dimensions AND together, exactly like two
 * fixed families do: "region = NL AND fuel_type = diesel". Two conditions on
 * the SAME dimension would too — but the panel never writes them, because its
 * field → condition mapping is one-to-one per key (values plus the "(empty)"
 * widening is a single condition).
 */
export function applyEmissionFactorFilterDocument(rows, doc) {
    if (doc.conditions.length === 0)
        return rows;
    return rows.filter((row) => doc.conditions.every((c) => matchesCondition(row, c)));
}
/** Count of active conditions — the filter button's badge. */
export function countActiveEmissionFactorConditions(doc) {
    return doc.conditions.length;
}
//# sourceMappingURL=emissionFactorFilters.js.map